import {
  useApi,
  type IssueFilteringDtoOptions,
  type IssueSortingDtoOptions,
  type Issues200Response,
  type IssueSearchFilteringDtoOptions,
} from '@issuemanager/api-client';
import { ExtendedAxiosError } from '@issuemanager/apps/client/hooks';
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { isSourceEntityFilterPresent } from './should-use-post-search';

export type ListIssuesQueryParameters = {
  sort?: IssueSortingDtoOptions;
  filter?: IssueSearchFilteringDtoOptions;
  page?: number;
  limit?: number;
};

export type UseInfiniteIssuesParams = {
  enabled?: boolean;
  /** When true, always routes through POST /issues/search regardless of filter shape. */
  usePost?: boolean;
} & ListIssuesQueryParameters;

export function useInfiniteIssues(props: UseInfiniteIssuesParams) {
  const {
    page = 0,
    limit = 10,
    sort = {},
    filter = {},
    enabled = true,
    usePost = false,
  } = props;

  const api = useApi();

  const postSearch = usePost || isSourceEntityFilterPresent(filter);

  const queryKey = ['issues', sort, filter, page, limit, enabled, postSearch];

  return useInfiniteQuery<
    Issues200Response,
    ExtendedAxiosError,
    InfiniteData<Issues200Response>,
    typeof queryKey,
    number
  >({
    queryKey,
    queryFn: ({ pageParam }) =>
      postSearch
        ? api.issuesApi
            .searchIssues({
              sort,
              filter: filter as IssueSearchFilteringDtoOptions,
              page: pageParam,
              limit,
            })
            .then((r) => r.data)
        : api.issuesApi
            .issues(sort, filter as IssueFilteringDtoOptions, pageParam, limit)
            .then((r) => r.data),
    enabled,
    initialPageParam: page,
    getNextPageParam: (lastPage) =>
      lastPage.meta.hasNextPage ? lastPage.meta.page + 1 : undefined,
  });
}
